/**
 * Product identity and the demo asset registry.
 *
 * Every customer-visible asset name lives here so UI copy never carries an OEM
 * or programme name. Ids are wire ids shared with the backend, localStorage and
 * the CAD manifests: change a label freely, never an id.
 */

export const PRODUCT = {
  name: 'Varahi MHI',
  expansion: 'Machine-to-Human Interface',
  assistant: 'MHI Assistant',
};

/** Knowledge-graph systems selectable in chat. Ids match the backend. */
export const CHAT_ASSETS = {
  nexon: {
    name: 'Light commercial EV',
    description: 'Technical breakdown & failure matrix',
    manual: "owner's manual",
    icon: '⚡',
    color: '#66B4C4',
  },
  nano: {
    name: 'City car',
    description: 'Support & maintenance manual',
    manual: 'support & maintenance manual',
    icon: '🔧',
    color: '#ff6b35',
  },
  compressor: {
    name: 'Rotary screw compressor',
    description: 'Compressor model book',
    manual: 'technical manual',
    icon: '⚙️',
    color: '#10b981',
  },
};

export const DEFAULT_CHAT_ASSET = 'nexon';

export const chatAssetName = (id) =>
  (CHAT_ASSETS[id] || CHAT_ASSETS[DEFAULT_CHAT_ASSET]).name;

const GROUND_VEHICLE_CAMERA = {
  position: [4.8, 2.9, 5.6], target: [0, 0.85, 0], maxDistance: 24, far: 200,
};

/**
 * Exploded-view assemblies. Each id resolves to public/cad/{id}_assembly.json.
 * Cameras are in scene units (metres): the jet is ~5x the size of the vehicles,
 * the vacuum pump skid about half.
 *
 * `knowledge` selects the knowledge interface (knowledge_packs/CONTRACT.md §4):
 *   'part'   — part-keyed dossier / trace / ask / resolve / coverage
 *   'legacy' — name-matched knowledge graph (PartDetailPanel)
 *   'none'   — geometry only
 */
export const EXPLODED_ASSETS = [
  {
    id: 'ace_ev',
    label: 'Light commercial EV',
    note: 'Synthetic CAD',
    domain: 'automotive',
    camera: GROUND_VEHICLE_CAMERA,
    // Legacy name-matched knowledge graph via /assembly/ace_ev/node.
    knowledge: 'legacy',
  },
  {
    id: 'yaris',
    label: 'Compact sedan',
    note: 'Public finite-element model (CCSA / FHWA)',
    domain: 'automotive',
    camera: GROUND_VEHICLE_CAMERA,
    knowledge: 'none',
  },
  {
    id: 'jet',
    label: 'Twin-engine jet',
    note: 'Synthetic CAD · fictional design',
    domain: 'aviation',
    camera: { position: [19, 10.5, 22], target: [0, 1.2, 0], maxDistance: 110, far: 1000 },
    // Part-keyed knowledge pack (knowledge_packs/CONTRACT.md §3).
    knowledge: 'part',
  },
  {
    id: 'vacuum_pump',
    label: 'Screw vacuum pump',
    note: 'Synthetic CAD · fictional design',
    // Shown in the dossier footer and trace header (cad/VACUUM_SPEC.md R5).
    knowledgeNote: 'Synthetic knowledge pack · fictional design',
    domain: 'industrial',
    // ~1.9 m skid; the canopy panels explode ~2.4 m out, so leave room to orbit.
    // From the rear-left and above (scene -Z is CAD +Y, scene -X the rear): the rotor pair,
    // gas ballast and non-return valve explode towards the viewer and the cabinet away, with
    // the left side panel off to one side; ~6.3 m out so the full explode stays in frame.
    camera: { position: [-3.8, 3.3, -4.45], target: [0, 1.0, 0], maxDistance: 16, far: 200 },
    // Some fault-cause units stay hidden at every rest level of this view (the thermostatic insert
    // behind the cooling pack, the controller behind the cabinet back plate): the workspace solos a
    // unit picked from the tree, a trace hypothesis or an answer (ExplodedView handlePick).
    isolateOnPick: true,
    knowledge: 'part',
    // Example the empty dossier suggests typing in the ask bar.
    examplePart: 'the non-return valve',
    // Plain-string synonyms of the server resolver block (knowledge_packs/vacuum_pump
    // pack.yaml), applied by localResolve.js when the knowledge service is down.
    // The conditional "the element" rule stays server-only.
    resolverPhrases: {
      'check valve': 'non return valve',
      nrv: 'non return valve',
      'screw element': 'vacuum element',
      'screw pair': 'rotors',
      screws: 'rotors',
      'pump element': 'vacuum element',
      'compression element': 'vacuum element',
      'air end': 'vacuum element',
      airend: 'vacuum element',
      'exhaust filter': 'separator element',
      coalescer: 'separator element',
      'coalescing element': 'separator element',
      'coalescing filter': 'separator element',
      'mist eliminator': 'primary demister',
      'variable speed drive': 'vsd inverter',
      'frequency converter': 'vsd inverter',
      vfd: 'vsd inverter',
      'drive electronics': 'vsd inverter',
      'acoustic hood': 'canopy',
      enclosure: 'canopy',
      hood: 'canopy',
      thermostat: 'thermostatic valve',
      radiator: 'oil cooler',
      'oil reservoir': 'separator vessel',
      'oil tank': 'separator vessel',
      sump: 'separator vessel',
      'change the oil': 'vessel shell oil',
      'changing the oil': 'vessel shell oil',
      'oil change': 'vessel shell oil',
      'main motor': 'drive motor',
      plc: 'main controller',
      'pump controller': 'main controller',
      'service door': 'access door',
      'rate of rise': 'rate of rise inlet isolation valve',
      hmi: 'controller display',
      // Keep "back" off the Cabinet Back Plate (same phrases as the pack's resolver).
      'back streaming': 'backstreaming',
      'suck back': 'suckback',
      'sucked back': 'suckback',
      'sucking back': 'suckback',
      'sucks back': 'suckback',
      'flowing back': 'backflow',
      'oil back': 'oil backflow',
      // Vacuum-service words and the safety chain's vocabulary (review round 3). The conditional
      // "solenoid" rule stays server-only too.
      'oil mist filter': 'separator element',
      muffler: 'exhaust silencer',
      'heat exchanger': 'oil cooler',
      'vent valve': 'vacuum gauge',
      'purge valve': 'inert gas purge port',
      'sample valve': 'oil drain valve',
      'sample point': 'oil drain valve',
      'sampling point': 'oil drain valve',
      'oil sample': 'oil drain valve',
      'inlet spool': 'inlet flange',
      spool: 'inlet flange',
      'inlet riser': 'inlet pipe',
      riser: 'inlet pipe',
      'after stop': 'after shutdown',
      'at stop': 'at shutdown',
      // No such parts: oil injection is pressure-driven.
      'oil pump': '',
      'flux capacitor': '',
    },
  },
];

export const explodedAssetFor = (id) =>
  EXPLODED_ASSETS.find((a) => a.id === id) ?? EXPLODED_ASSETS[0];

/**
 * Label for the contract's `ata` grouping code. Industrial assets carry their
 * own system codes (V10..V83) in that field, so they read "System V32".
 */
export const codeLabelFor = (domain) => (domain === 'industrial' ? 'System' : 'ATA');

/** Heading for an asset's fault list in the dossier. */
export const faultListLabelFor = (domain) => (domain === 'industrial' ? 'Alarms' : 'Maintenance messages');
